import CTAS from 'react-scale/components/elements/cta';
import Logo from 'react-scale/components/website/Logo';
import Video from 'react-scale/components/elements/video';
import Image from 'next/image';
import cn from 'classnames';
import MeshGradient from 'react-scale/components/elements/webgl/mesh-gradient';
import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import styles from './hero.module.css';

const HeroVideo = ({ className, src, poster, muted }) => (
  <div className={cn('text-center w-full', className)}>
    <div className="relative w-full lg:hidden">
      <Video url={src} blackButton poster={poster} smallButton muted={muted} />
    </div>
    <div className="hidden max-w-3xl cursor-pointer lg:block xl:w-full">
      <Video url={src} blackButton poster={poster} smallButton muted={muted} />
    </div>
  </div>
);

const HeroImageOverflowing = ({ className, asset }) => (
  <div className={cn('relative text-center w-full max-w-max', className)}>
    <div
      className={cn(
        styles.heroImage,
        'relative p-3 mx-auto flex bg-white backdrop-blur-xl bg-opacity-20 rounded-2xl w-full lg:hidden max-w-max'
      )}
    >
      <Image
        className="rounded-xl"
        quality={100}
        layout="intrinsic"
        width={asset.width}
        height={asset.height}
        src={asset.src}
        alt={asset.alt}
        priority
      />
    </div>
    <div
      className={cn(
        styles.heroImage,
        'relative grow-0 p-6 bg-white backdrop-blur-xl bg-opacity-20 rounded-2xl hidden lg:flex'
      )}
    >
      <Image
        className="rounded-xl"
        quality={100}
        layout="fixed"
        width={asset.width}
        height={asset.height}
        src={asset.src}
        alt={asset.alt}
        priority
      />
    </div>
  </div>
);

const HeroProduct = ({ logo, title, subtitle, asset, cta, spacing, video }) => {
  const [shownAsset, setShownAsset] = useState();

  useEffect(() => {
    if (video?.src) {
      setShownAsset(<HeroVideo src={video.src} poster={video.poster} muted={video.muted} />);
    } else if (asset?.overflowing) {
      setShownAsset(
        <div className="ml-auto max-w-max">
          <HeroImageOverflowing
            asset={asset}
            className={cn({ 'lg:ml-32': spacing === 'md', 'lg:ml-8': spacing === 'sm' })}
          />
        </div>
      );
    } else if (!asset?.overflowing && !video?.src && asset?.src) {
      setShownAsset(
        <div className="flex mt-16 ml-auto lg:ml-10 lg:mt-0 max-w-max">
          <Image
            className="rounded-lg"
            src={asset.src}
            layout="intrinsic"
            alt={asset.alt}
            width={asset.width}
            height={asset.height}
            quality={100}
          />
        </div>
      );
    } else return null;
  }, []);

  return (
    <div className="relative overflow-hidden">
      <div className="absolute z-0 inset-0 overflow-hidden max-w-[100vw]">
        <div className="absolute inset-0 w-full bg-gradient-to-b from-black" />
        <div className="absolute inset-0 w-full bottom-2/3 bg-gradient-to-b from-black" />
        <MeshGradient
          style={{
            '--gradient-color-1': '#D1AAD7',
            '--gradient-color-2': '#5933b2',
            '--gradient-color-3': '#8349FF',
            '--gradient-color-4': '#7B8CE7'
          }}
        />
      </div>
      <div className="relative">
        <div
          className={cn(
            'container flex justify-between items-center w-full py-18',
            asset?.overflowing ? 'lg:pt-24 lg:pb-32' : 'lg:pt-40 lg:pb-44'
          )}
        >
          <div className="relative w-full lg:w-auto lg:min-w-max flex-grow-1">
            <div className={styles.radialBg} />
            <div className="relative lg:max-w-[487px]">
              <div className="flex flex-col items-center justify-center lg:flex-row lg:items-stretch lg:justify-start">
                <span className="mb-6 lg:hidden">
                  <Image
                    src={logo?.src}
                    alt={logo?.alt}
                    width={logo?.widthMobile ?? '70'}
                    height={logo?.heightMobile ?? '81'}
                  />
                </span>
                <span className="hidden select-none lg:flex">
                  <Image
                    src={logo?.src}
                    width={logo?.width}
                    height={logo?.height}
                    alt={logo?.alt}
                  />
                </span>
                <div
                  className={cn(
                    'flex flex-col lg:block justify-center items-center',
                    asset?.src || video?.src ? 'lg:ml-10' : 'lg:ml-14'
                  )}
                >
                  <div className="mb-4 lg:hidden">
                    <Logo width="68px" fillmobile="#A3A3A3" />
                  </div>
                  <div
                    className={cn('hidden lg:block', asset?.src || video?.src ? 'mb-6' : 'mb-12')}
                  >
                    <Logo width={cn(asset?.src || video?.src ? '82px' : '118px')} fill="#A3A3A3" />
                  </div>
                  <h1
                    className={cn(
                      'font-aeonik text-4xl leading-[45px] lg:leading-10',
                      asset?.src || video?.src ? 'md:text-[52px]' : 'md:text-5.5xl lg:text-7xl'
                    )}
                  >
                    {title}
                  </h1>
                </div>
              </div>
              <ReactMarkdown
                rehypePlugins={[rehypeRaw]}
                className={cn(
                  'my-12 sm:px-0 text-lg leading-8 text-center lg:text-left lg:text-2xl lg:leading-10 lg:font-aeonik',
                  asset?.src || video?.src ? 'lg:mt-8' : 'lg:mt-10'
                )}
              >
                {subtitle}
              </ReactMarkdown>
              <div className="flex lg:hidden">{shownAsset}</div>
              <div
                className={cn(
                  'flex mx-auto mt-16 lg:mt-10 gap-x-8 w-full sm:max-w-max lg:max-w-none',
                  asset?.src || video?.src ? 'lg:mt-7' : 'lg:mt-12'
                )}
              >
                <CTAS cta={cta} />
              </div>
            </div>
          </div>
          <div className="hidden w-full lg:flex">{shownAsset}</div>
        </div>
      </div>
    </div>
  );
};

export default HeroProduct;
